// Slice 2C4 · Placeholder, example-data, and fake-contact protection.
//
// One shared client-side validator matching the narrow server registry in
// `backend/placeholder_validation.py`. Used by:
//   * DocumentDefaultsFamilyCard (per-family Save pre-check + server-error mapping)
//   * Step3DocumentDefaults (server-computed family_validation display + status blocker)
//   * DocumentDefaults / CloseoutDefaults standalone pages (per-family Save pre-check)
//   * SetUpYourBusiness · Business & Trades step (Company Profile pre-check)
//
// Contract:
//   * Explicit empty string ("") is ALWAYS valid.
//   * Legitimate contractor names, phone numbers, emails, and websites
//     are ALWAYS valid.
//   * Rejections are narrow, explainable, and stable — same rejection
//     the server would emit.

export const ERROR_CODE = "placeholder_data_not_allowed";
export const ERROR_MESSAGE =
  "Replace this example information or leave the field blank to use JobClose AI\u2019s built-in default.";
export const CONTACT_ERROR_MESSAGE =
  "Enter your real business contact information, or leave this optional field blank.";
export const BANNER_MESSAGE =
  "This section contains example information that could appear on a customer document.";
export const STATUS_BLOCKER_HELPER =
  "Replace the example information or clear the affected field before resolving this section.";

// ---------- Registry — must mirror placeholder_validation.py ----------
const KNOWN_PLACEHOLDER_TOKENS = [
  "[company]",
  "[company name]",
  "[your company]",
  "[phone]",
  "[phone number]",
  "[email]",
  "[email address]",
  "[name]",
  "[address]",
  "[website]",
  "{{company}}",
  "{{company_name}}",
  "{{phone}}",
  "{{email}}",
  "<company>",
  "<company name>",
  "<phone>",
  "<email>",
];

const KNOWN_FAKE_EMAILS = [
  "office@company.com",
  "info@company.com",
  "name@example.com",
  "email@example.com",
  "test@example.com",
  "you@example.com",
  "your@email.com",
];

const KNOWN_FAKE_PHONE_DIGITS = ["5555555555", "1234567890", "0000000000"];

const KNOWN_FAKE_BUSINESS_NAMES = [
  "[company]",
  "[company name]",
  "your company",
  "company name",
  "example company",
];

const INSTRUCTIONAL_PLACEHOLDERS = [
  "enter the opening paragraph new proposals should begin with.",
  "enter closing scope notes or standard fine print used on your proposals.",
  "explain your deposit requirement in your own words.",
  "explain when the final payment is expected.",
  "explain your policy for balances past their due date.",
  "enter your company's standard contract terms.",
  "enter limitations, exclusions, or responsibilities specific to your trade.",
  "enter the acknowledgment shown above the signature line.",
  "enter the standard notes that appear on new invoices.",
  "enter the company name and contact details customers should use to submit payment.",
  "enter the team name and the phone/email the customer should use.",
  "enter the wording you use to ask for a review.",
  "enter the wording you use to ask for referrals.",
  "explain the length and scope of your workmanship warranty.",
  "explain how manufacturer warranties are handled for materials you install.",
  "explain the care steps customers should follow after the job is complete.",
  "enter the company name and contact details customers should use for warranty or service questions.",
  "enter the standard closing wording used on new packets.",
  "enter the wording you use to request a review after completion.",
  "enter the wording you use to request referrals after completion.",
];

const QUOTE_MAP = {
  "\u2018": "'",
  "\u2019": "'",
  "\u201C": '"',
  "\u201D": '"',
  "\u2013": "-",
  "\u2014": "-",
};

const PHONE_RE = /\(?\d{3}\)?[\s.\-]?\d{3}[\s.\-]?\d{4}/g;

const normalizeText = (raw) => {
  if (typeof raw !== "string") return "";
  let v = raw;
  for (const src of Object.keys(QUOTE_MAP)) v = v.split(src).join(QUOTE_MAP[src]);
  v = v.trim().toLowerCase();
  v = v.replace(/\s+/g, " ");
  return v;
};

const digitsOnly = (raw) => (typeof raw === "string" ? raw.replace(/\D+/g, "") : "");

const containsPlaceholderToken = (raw) => {
  const norm = normalizeText(raw);
  if (!norm) return false;
  return KNOWN_PLACEHOLDER_TOKENS.some((tok) => norm.includes(tok));
};

const matchesInstructionalPlaceholder = (raw) => {
  const norm = normalizeText(raw);
  if (!norm) return false;
  return INSTRUCTIONAL_PLACEHOLDERS.includes(norm);
};

const containsFakeEmail = (raw) => {
  const norm = normalizeText(raw);
  if (!norm) return false;
  return KNOWN_FAKE_EMAILS.some((fake) => norm.includes(fake));
};

const matchesFakeEmailWhole = (raw) =>
  KNOWN_FAKE_EMAILS.includes(normalizeText(raw));

const containsFakePhone = (raw) => {
  if (typeof raw !== "string" || !raw) return false;
  const matches = raw.match(PHONE_RE) || [];
  return matches.some((m) => KNOWN_FAKE_PHONE_DIGITS.includes(digitsOnly(m)));
};

const matchesFakePhoneWhole = (raw) => {
  const digits = digitsOnly(raw);
  if (!digits) return false;
  return KNOWN_FAKE_PHONE_DIGITS.includes(digits);
};

const matchesFakeBusinessName = (raw) =>
  KNOWN_FAKE_BUSINESS_NAMES.includes(normalizeText(raw));

// ---------- Per-field-kind API ----------
export const validateField = (kind, value) => {
  if (value === null || value === undefined) return "";
  if (typeof value !== "string") return "";
  if (!value.trim()) return ""; // explicit empty is valid

  if (kind === "email") {
    return matchesFakeEmailWhole(value) ? CONTACT_ERROR_MESSAGE : "";
  }
  if (kind === "phone") {
    return matchesFakePhoneWhole(value) ? CONTACT_ERROR_MESSAGE : "";
  }
  if (kind === "business") {
    if (matchesFakeBusinessName(value)) return CONTACT_ERROR_MESSAGE;
    if (matchesInstructionalPlaceholder(value)) return ERROR_MESSAGE;
    if (containsPlaceholderToken(value)) return ERROR_MESSAGE;
    return "";
  }
  if (kind === "website") {
    return containsPlaceholderToken(value) ? ERROR_MESSAGE : "";
  }

  // default "text"
  if (matchesInstructionalPlaceholder(value)) return ERROR_MESSAGE;
  if (containsPlaceholderToken(value)) return ERROR_MESSAGE;
  if (containsFakeEmail(value)) return CONTACT_ERROR_MESSAGE;
  if (containsFakePhone(value)) return CONTACT_ERROR_MESSAGE;
  return "";
};

// ---------- Field-kind registries (mirror backend) ----------
export const DOCUMENT_DEFAULTS_FIELD_KINDS = {
  proposal_intro: "text",
  proposal_footer: "text",
  deposit_terms: "text",
  final_payment_terms: "text",
  late_payment_note: "text",
  contract_terms: "text",
  contractor_disclaimer: "text",
  customer_approval_language: "text",
  invoice_notes: "text",
  payment_instructions: "text",
  customer_update_signature: "text",
  review_request: "text",
  referral_request: "text",
};

export const CLOSEOUT_DEFAULTS_FIELD_KINDS = {
  workmanship_warranty_terms: "text",
  manufacturer_warranty_notes: "text",
  maintenance_notes: "text",
  warranty_contact_notes: "text",
  closeout_notes_template: "text",
  review_request: "text",
  referral_request: "text",
};

export const COMPANY_PROFILE_FIELD_KINDS = {
  company_name: "business",
  phone: "phone",
  email: "email",
  website: "website",
};

// Family owner map — matches backend _DOC_FAMILY_FIELD_MAP.
export const FAMILY_FIELD_KINDS = {
  proposal: {
    proposal_intro: "text",
    proposal_footer: "text",
    deposit_terms: "text",
    final_payment_terms: "text",
    late_payment_note: "text",
  },
  contract: {
    contract_terms: "text",
    contractor_disclaimer: "text",
    customer_approval_language: "text",
  },
  invoice: {
    invoice_notes: "text",
    payment_instructions: "text",
  },
  customer_update: {
    customer_update_signature: "text",
    review_request: "text",
    referral_request: "text",
  },
  closeout: {
    workmanship_warranty_terms: "text",
    manufacturer_warranty_notes: "text",
    maintenance_notes: "text",
    warranty_contact_notes: "text",
    closeout_notes_template: "text",
    review_request: "text",
    referral_request: "text",
  },
};

// Validate a payload (or family value slice) against a field-kind map.
// Returns { fieldKey: message } for only the fields with issues.
export const validatePayload = (payload, fieldKinds) => {
  const out = {};
  if (!payload || typeof payload !== "object") return out;
  for (const k of Object.keys(payload)) {
    const kind = (fieldKinds && fieldKinds[k]) || "text";
    const msg = validateField(kind, payload[k]);
    if (msg) out[k] = msg;
  }
  return out;
};

export const validateFamilyValues = (familyKey, values) => {
  const kinds = FAMILY_FIELD_KINDS[familyKey] || {};
  const src = values || {};
  const out = {};
  // Iterate the family's owned keys ONLY — never validate stray keys
  // from a broader values object. Slice 2C3 family scoping guarantee.
  for (const k of Object.keys(kinds)) {
    const msg = validateField(kinds[k], src[k]);
    if (msg) out[k] = msg;
  }
  return out;
};

// Map a server 422 payload (or Axios error response) into the same
// { fieldKey: message } shape used above so the UI has a single format.
export const mapServerFieldErrors = (err) => {
  try {
    const detail = err && err.response && err.response.data && err.response.data.detail;
    if (!detail || typeof detail !== "object") return {};
    if (detail.code !== ERROR_CODE) return {};
    const fe = detail.field_errors || {};
    if (typeof fe !== "object") return {};
    const out = {};
    for (const k of Object.keys(fe)) {
      const v = fe[k];
      if (typeof v === "string" && v) out[k] = v;
    }
    return out;
  } catch (_e) {
    return {};
  }
};

// Convenience: does the current family have any blocking validation issues?
export const familyHasBlockingIssues = (familyKey, values) => {
  const errs = validateFamilyValues(familyKey, values);
  return Object.keys(errs).length > 0;
};
